import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import styles from './ShowcaseGrid.module.sass';
import { getCloudinaryUrl } from '../../utils/cloudinary';

const PROVIDER_LABEL = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
};

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const optimizedTile = (url) =>
  getCloudinaryUrl(url, { width: 1080, height: 1080, crop: 'fill', quality: 'auto', format: 'auto' });

const optimizedFull = (url) =>
  getCloudinaryUrl(url, { width: 1920, quality: 'auto', format: 'auto' });

const ShowcaseGrid = ({ assets, className, heading = 'Showcase' }) => {
  const [modalUrl, setModalUrl] = useState(null);

  useEffect(() => {
    if (!modalUrl) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setModalUrl(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [modalUrl]);

  const validAssets = Array.isArray(assets)
    ? assets.filter((a) => a && (a.thumbnail_url || a.thumbnailUrl || a.image_url || a.imageUrl))
    : [];

  if (validAssets.length === 0) return null;

  const handleTileClick = (asset) => {
    const isReel = asset.asset_type === 'reel' || asset.assetType === 'reel';
    if (isReel) {
      const reelUrl = asset.reel_url || asset.reelUrl;
      if (reelUrl) window.open(reelUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    const fullUrl = asset.image_url || asset.imageUrl;
    if (fullUrl) setModalUrl(fullUrl);
  };

  return (
    <section className={cn(styles.section, className)} aria-label={heading}>
      {heading && <h3 className={styles.heading}>{heading}</h3>}
      <div className={styles.grid}>
        {validAssets.map((asset) => {
          const isReel = asset.asset_type === 'reel' || asset.assetType === 'reel';
          const provider = asset.reel_provider || asset.reelProvider;
          const thumb = asset.thumbnail_url || asset.thumbnailUrl || asset.image_url || asset.imageUrl;
          const optimized = optimizedTile(thumb) || thumb;

          const ariaLabel = isReel
            ? `Open ${PROVIDER_LABEL[provider] || 'reel'} link in a new tab`
            : 'Expand showcase photo';

          return (
            <button
              key={asset.id}
              type="button"
              className={styles.tile}
              onClick={() => handleTileClick(asset)}
              aria-label={ariaLabel}
            >
              <img src={optimized} alt="" className={styles.image} loading="lazy" />
              {isReel && (
                <>
                  <span className={styles.providerBadge}>
                    {PROVIDER_LABEL[provider] || 'Reel'}
                  </span>
                  <span className={styles.reelOverlay}>
                    <span className={styles.playIcon}>
                      <PlayIcon />
                    </span>
                  </span>
                </>
              )}
            </button>
          );
        })}
      </div>

      {modalUrl && createPortal(
        <div className={styles.imageModalOverlay} onClick={() => setModalUrl(null)}>
          <div className={styles.imageModal} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={styles.modalClose}
              onClick={() => setModalUrl(null)}
            >
              Close
            </button>
            <img
              src={optimizedFull(modalUrl) || modalUrl}
              alt="Showcase"
              className={styles.modalImage}
            />
          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

export default ShowcaseGrid;
